import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { X, ChevronRight, ChevronLeft, Sparkles, FileText, Image, Send, CheckCircle } from 'lucide-react';

const TOUR_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to Memorial Desk! 🎉',
    description: 'This quick tour will show you how to manage memorial orders from start to finish. You have full access to try everything!',
    icon: Sparkles,
    position: 'center',
    highlight: null
  },
  {
    id: 'dashboard',
    title: 'Your Dashboard',
    description: 'This is your command center. View all orders, filter by type (New Memorial, Restoration, Add-on Inscription), and track their status at a glance.',
    icon: FileText,
    position: 'center',
    highlight: '[data-testid="orders-grid"]'
  },
  {
    id: 'order-detail',
    title: 'Click Any Order',
    description: 'Click on an order card to see full details. You can edit client info, update the stone specifications, change status, and manage the entire workflow.',
    icon: FileText,
    position: 'center',
    highlight: null
  },
  {
    id: 'quotes',
    title: 'Create Quotes',
    description: 'In the order detail, go to the Quotes tab and click "Create Quote". Items are pre-populated from the order - just review and send to your client!',
    icon: FileText,
    position: 'center',
    highlight: null
  },
  {
    id: 'artwork',
    title: 'Upload Artwork',
    description: 'In the Artwork tab, upload your memorial designs. When you click "Send", you\'ll be asked for YOUR email so you can see exactly what clients receive - including approve/reject buttons!',
    icon: Image,
    position: 'center',
    highlight: null
  },
  {
    id: 'email-demo',
    title: 'Try the Email Workflow',
    description: 'When sending quotes, invoices, or artwork, enter your real email address. You\'ll receive the actual email with all formatting and action buttons - perfect for testing the client experience!',
    icon: Send,
    position: 'center',
    highlight: null
  },
  {
    id: 'settings',
    title: 'Explore Settings',
    description: 'Click "Settings" to manage sundries, branches, and lettering costs. Check out "Stock" for inventory management and "Users" to see team management.',
    icon: CheckCircle,
    position: 'center',
    highlight: '[data-testid="settings-btn"]'
  },
  {
    id: 'finish',
    title: 'You\'re All Set! 🚀',
    description: 'Remember: all changes reset when you log out, so feel free to experiment! Ready to streamline your memorial business? Click "Start Free Trial" when you\'re done exploring.',
    icon: Sparkles,
    position: 'center',
    highlight: null
  }
];

function DemoTour({ onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    // Check if tour was already completed
    const tourCompleted = localStorage.getItem('demo_tour_completed');
    if (tourCompleted) {
      setIsVisible(false);
      if (onComplete) onComplete();
    }
  }, [onComplete]);

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    localStorage.setItem('demo_tour_completed', 'true');
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  if (!isVisible) return null;

  const step = TOUR_STEPS[currentStep];
  const StepIcon = step.icon;
  const isLastStep = currentStep === TOUR_STEPS.length - 1;
  const isFirstStep = currentStep === 0;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      
      {/* Tour Card */}
      <Card className="relative z-10 w-full max-w-lg mx-4 shadow-2xl border-2 border-blue-200 animate-in fade-in zoom-in duration-300">
        <CardContent className="p-0">
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <StepIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-blue-100 text-sm font-medium">
                    Step {currentStep + 1} of {TOUR_STEPS.length}
                  </p>
                  <h3 className="text-xl font-bold text-white">{step.title}</h3>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSkip}
                className="text-white/70 hover:text-white hover:bg-white/10"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <p className="text-slate-600 text-base leading-relaxed mb-6">
              {step.description}
            </p>

            {/* Progress dots */}
            <div className="flex justify-center gap-2 mb-6">
              {TOUR_STEPS.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentStep(index)}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-200 ${
                    index === currentStep 
                      ? 'bg-blue-600 w-6' 
                      : index < currentStep 
                        ? 'bg-blue-300' 
                        : 'bg-slate-200'
                  }`}
                />
              ))}
            </div>

            {/* Navigation buttons */}
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                onClick={handlePrev}
                disabled={isFirstStep}
                className={isFirstStep ? 'invisible' : ''}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>

              <Button
                variant="outline"
                onClick={handleSkip}
                className="text-slate-500"
              >
                Skip Tour
              </Button>

              <Button
                onClick={handleNext}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isLastStep ? (
                  <>
                    Get Started
                    <Sparkles className="w-4 h-4 ml-1" />
                  </>
                ) : (
                  <>
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default DemoTour;
